import React from "react";
import { Check } from "lucide-react";

/**
 * L'identita' visiva dell'onboarding (PromptIntro, CreationLoader,
 * AnalysisReview) riusata in Apps & Integrazioni: stessi colori, stesso mesh
 * gradient a blob, stesse card, stessa freccia disegnata a mano.
 */

// Il gradiente di fondo e i blob sono quelli del loader di creazione.
export const MESH_BG = "linear-gradient(146deg, #F0F4FF 0%, #F0FFF4 50%, #F8F0FF 100%)";

// Un gradiente per app, preso dai blob: blu-viola e verde.
export const TINTS = {
  violet: "linear-gradient(135deg, #4285F4, #9C4EFF)",
  green: "linear-gradient(135deg, #34A853, #0F9D58)",
  blue: "linear-gradient(135deg, #60A5FA, #4285F4)",
  sky: "linear-gradient(135deg, #38BDF8, #0EA5E9)",
  pink: "linear-gradient(135deg, #F472B6, #A855F7)",
  orange: "linear-gradient(135deg, #FB923C, #F97316)",
  amber: "linear-gradient(135deg, #FBBF24, #F59E0B)",
  slate: "linear-gradient(135deg, #CBD5E1, #94A3B8)",
};

// Il colore principale di ciascun gradiente, per le superfici colorate.
export const TINT_COLORS = {
  violet: "#8B5CF6",
  green: "#0F9D58",
  blue: "#4285F4",
  sky: "#0EA5E9",
  pink: "#EC4899",
  orange: "#F97316",
  amber: "#F59E0B",
  slate: "#94A3B8",
};

/**
 * Il marchio ufficiale di un servizio (WhatsApp, Google, Telegram...), da
 * simple-icons: logo in un colore e colore del brand. `sfondo` sostituisce il
 * colore pieno quando il brand usa un gradiente (Instagram).
 */
export function marchio(icona, { sfondo } = {}) {
  return { path: icona.path, hex: `#${icona.hex}`, title: icona.title, sfondo };
}

/** Il colore principale di un'app: quello del brand o quello del suo gradiente. */
export function coloreApp(app) {
  return app.brand?.hex || TINT_COLORS[app.tint] || TINT_COLORS.violet;
}

/**
 * Lo sfondo di una scheda: bianco, con il colore dell'app come un alone
 * nell'angolo in alto a destra che sfuma nel bianco. Il colore vero lo porta
 * l'icona.
 */
export function tintSurface(colore) {
  const c = colore || TINT_COLORS.violet;
  return {
    backgroundColor: "#FFFFFF",
    backgroundImage: `radial-gradient(95% 70% at 100% 0%, ${c}4D 0%, ${c}1A 38%, transparent 68%)`,
  };
}

/**
 * Variabili della dashboard ridefinite solo dentro Apps & Integrazioni: il
 * contorno di focus (--ring) e' grigio chiaro invece del colore primario,
 * cosi' campi, interruttori e card non si accendono di blu scuro.
 */
export const VARIABILI_SEZIONE = { "--ring": "215 20% 72%" };

/**
 * Regole di stile delle pagine di impostazioni con anteprima (pagina Widget):
 * le distanze e i colori decisi una volta, da usare invece dei numeri.
 * Classi Tailwind; i "!" vincono sugli space-y dei contenitori.
 */
export const SPAZI = {
  // 40px tra la barra delle schede e il punto 1.
  schedeContenuto: "!mt-10",
  // 52px tra un punto e l'altro, senza linee di divisione.
  traPassi: "space-y-[52px]",
  // 20px tra il titolo del punto (numero, titolo, sottotitolo) e i campi.
  titoloCampi: "space-y-5",
  // 100px tra la colonna dei campi e l'anteprima.
  campiAnteprima: "gap-[100px]",
  // 60px tra l'ultimo campo e la barra di salvataggio.
  sopraBarraSalvataggio: "!mt-[60px]",
};

// Il fondo dei box di codice (snippet): lo stesso grigio della barra laterale.
export const FONDO_CODICE = "bg-[#F8FAFC]";

// Azioni secondarie (Copia, URL diretto): solo icona e testo, senza fondo.
// L'azione principale resta il bottone nero (SCURO in casella.jsx).
export const TESTO_AZIONE = "text-[#18181B] hover:text-[#64748B]";
// Dopo aver copiato, per un attimo.
export const TESTO_COPIATO = "text-[#16A34A]";

/** Animazioni e carattere a mano, una volta per pagina. */
export function BrandStyles() {
  return (
    <style>{`
      @import url('https://fonts.googleapis.com/css2?family=Fuzzy+Bubbles:wght@700&display=swap');
      @keyframes puBlobA { from { transform: translate(0,0) scale(1); } to { transform: translate(60px,40px) scale(1.15); } }
      @keyframes puBlobB { from { transform: translate(0,0) scale(1.1); } to { transform: translate(-50px,-60px) scale(0.95); } }
      @keyframes puBlobC { from { transform: translate(0,0) scale(0.95); } to { transform: translate(40px,50px) scale(1.12); } }
      @keyframes puFadeUp { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: translateY(0); } }
      @keyframes puPopIn { from { opacity: 0; transform: scale(0.4); } to { opacity: 1; transform: scale(1); } }
      @keyframes puCartaIn {
        from { opacity: 0; transform: translateY(10px) scale(.985); filter: blur(3px); }
        to { opacity: 1; transform: none; filter: none; }
      }
      /* Le schede entrano una dopo l'altra: --ritardo lo mette la griglia. */
      .pu-carta-in { animation: puCartaIn .18s cubic-bezier(.22,1,.36,1) both; animation-delay: var(--ritardo, 0ms); }
      /* L'uscita e' l'entrata al contrario: stessa durata, curva speculare,
         ordine inverso (--ritardo-uscita). */
      @keyframes puCartaOut {
        from { opacity: 1; transform: none; filter: none; }
        to { opacity: 0; transform: translateY(10px) scale(.985); filter: blur(3px); }
      }
      .pu-uscita .pu-carta-in { animation: puCartaOut .18s cubic-bezier(.64,0,.78,0) both; animation-delay: var(--ritardo-uscita, 0ms); }
      .pu-blob-a { animation: puBlobA 16s ease-in-out infinite alternate; }
      .pu-blob-b { animation: puBlobB 18s ease-in-out infinite alternate; }
      .pu-blob-c { animation: puBlobC 20s ease-in-out infinite alternate; }
      .pu-fade-up { animation: puFadeUp .5s ease-out both; }
      .pu-pop-in { animation: puPopIn .3s ease-out both; }
      @media (prefers-reduced-motion: reduce) {
        .pu-blob-a, .pu-blob-b, .pu-blob-c, .pu-fade-up, .pu-pop-in, .pu-carta-in, .pu-uscita .pu-carta-in { animation: none; }
      }
    `}</style>
  );
}

/** Il mesh gradient con i tre blob sfocati, dentro un riquadro. */
export function MeshPanel({ className = "", children }) {
  return (
    <div className={`relative overflow-hidden ${className}`} style={{ backgroundImage: MESH_BG }}>
      <div
        className="pu-blob-a pointer-events-none absolute -left-10 top-6 size-[260px] rounded-full opacity-20 blur-[57px]"
        style={{ backgroundImage: TINTS.violet }}
      />
      <div
        className="pu-blob-b pointer-events-none absolute right-[8%] top-[40%] size-[220px] rounded-full opacity-15 blur-[57px]"
        style={{ backgroundImage: TINTS.green }}
      />
      <div
        className="pu-blob-c pointer-events-none absolute left-[45%] -top-24 size-[240px] rounded-full opacity-15 blur-[72px]"
        style={{ backgroundImage: "linear-gradient(135deg, #9C4EFF, #4285F4)" }}
      />
      <div className="relative">{children}</div>
    </div>
  );
}

/**
 * La sfumatura verso il bianco: una sola curva morbida (coseno, senza
 * gradini) che parte sotto il contenuto dell'intestazione e arriva al bianco
 * dopo `spazio + coda` px, dietro le schede. Tanti punti intermedi, cosi'
 * non si vedono bande.
 */
function sfumatura(spazio, coda) {
  const lunga = spazio + coda;
  const punti = [];
  for (let i = 0; i <= 24; i += 1) {
    const t = i / 24;
    const opacita = (0.5 * (1 + Math.cos(Math.PI * t))).toFixed(3);
    punti.push(`rgba(0,0,0,${opacita}) calc(100% - ${Math.round(lunga * (1 - t))}px)`);
  }
  return `linear-gradient(to bottom, #000 0, ${punti.join(", ")})`;
}

const svg = (contenuto, w, h) =>
  `url("data:image/svg+xml,${encodeURIComponent(
    `<svg xmlns='http://www.w3.org/2000/svg' width='${w}' height='${h}'>${contenuto}</svg>`
  )}")`;

// Antracite: scuro a sinistra, piu' chiaro verso destra (secondo riferimento).
const ANTRACITE =
  "linear-gradient(100deg, #1F1F1F 0%, #2B2B2B 22%, #3A3A3A 45%, #474747 65%, #535353 82%, #5A5A5A 100%)";
// Una grana leggerissima: evita le strisce che i gradienti scuri fanno sugli schermi.
const GRANA = svg(
  "<filter id='g'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(#g)'/>",
  240,
  240
);

/**
 * Un campo di stelle: puntini bianchi sparsi, sempre gli stessi (generatore
 * con seme fisso), su una tessera che si ripete.
 */
function campoStelle({ quante, w, h, seme, raggio, luce }) {
  let s = seme;
  const caso = () => (s = (s * 16807) % 2147483647) / 2147483647;
  let punti = "";
  for (let i = 0; i < quante; i += 1) {
    const x = (caso() * w).toFixed(1);
    const y = (caso() * h).toFixed(1);
    const r = (raggio[0] + caso() * (raggio[1] - raggio[0])).toFixed(2);
    const o = (luce[0] + caso() * (luce[1] - luce[0])).toFixed(2);
    punti += `<circle cx='${x}' cy='${y}' r='${r}' fill='white' fill-opacity='${o}'/>`;
  }
  return svg(punti, w, h);
}

// Tante stelle fioche e poche piu' luminose (queste ultime brillano).
const STELLE = campoStelle({ quante: 150, w: 720, h: 480, seme: 7, raggio: [0.35, 0.8], luce: [0.2, 0.6] });
const STELLE_VIVE = campoStelle({ quante: 22, w: 720, h: 480, seme: 91, raggio: [0.7, 1.1], luce: [0.7, 1] });

/** Cielo quasi nero con le stelle, aloni chiari e un bagliore rosso a sinistra. */
function Stelle() {
  return (
    <>
      <style>{`
        @keyframes puBrilla { 0%, 100% { opacity: .35; } 50% { opacity: 1; } }
        .pu-brilla { animation: puBrilla 5s ease-in-out infinite; }
        @media (prefers-reduced-motion: reduce) { .pu-brilla { animation: none; } }
      `}</style>
      {/* Gli aloni si misurano dall'alto: restano dietro all'intestazione,
          sotto lo sfondo continua nella sfumatura. */}
      <div
        className="pu-blob-a absolute -top-48 right-[2%] size-[620px] rounded-full blur-[70px]"
        style={{ background: "radial-gradient(circle, rgba(255,255,255,0.30) 0%, rgba(255,255,255,0.08) 45%, transparent 70%)" }}
      />
      <div
        className="absolute left-[6%] top-[200px] size-[380px] rounded-full blur-[60px]"
        style={{ background: "radial-gradient(circle, rgba(255,255,255,0.10) 0%, transparent 70%)" }}
      />
      <div
        className="absolute -left-10 top-[90px] size-24 rounded-full blur-[22px]"
        style={{ background: "radial-gradient(circle, rgba(220,38,38,0.55) 0%, transparent 70%)" }}
      />
      <div className="absolute inset-0" style={{ backgroundImage: STELLE }} />
      <div className="pu-brilla absolute inset-0" style={{ backgroundImage: STELLE_VIVE }} />
    </>
  );
}

/** Antracite liscio, con l'angolo in basso a destra un filo piu' chiaro. */
function Antracite() {
  return (
    <div
      className="absolute -right-40 top-[120px] h-[460px] w-[760px] rounded-full blur-[60px]"
      style={{ background: "radial-gradient(ellipse, rgba(255,255,255,0.10) 0%, transparent 70%)" }}
    />
  );
}

// Perla: grigio chiaro, piu' luminoso a sinistra e piu' scuro e caldo a destra.
const PERLA =
  "linear-gradient(110deg, #D6D6D6 0%, #C7C6C4 28%, #B8B7B5 52%, #AFADAB 72%, #B3AEAB 100%)";

/**
 * Le luci del grigio perla (terzo riferimento): chiaro in alto a sinistra,
 * caldo e chiaro in basso verso sinistra, una punta rosata in basso a destra.
 * Misurate sull'intestazione: sotto lo sfondo continua nella sfumatura.
 */
function Perla() {
  return (
    <div
      className="absolute inset-x-0 top-0 h-[640px]"
      style={{
        // Sfuma anche il riquadro delle luci, se no gli aloni si tagliano in fondo.
        maskImage: "linear-gradient(to bottom, #000 55%, transparent 100%)",
        WebkitMaskImage: "linear-gradient(to bottom, #000 55%, transparent 100%)",
        backgroundImage: [
          "radial-gradient(38% 55% at 0% 0%, rgba(224,224,224,0.9) 0%, transparent 70%)",
          "radial-gradient(55% 55% at 28% 72%, rgba(208,204,200,0.85) 0%, transparent 70%)",
          "radial-gradient(45% 50% at 88% 66%, rgba(186,174,170,0.7) 0%, transparent 70%)",
        ].join(", "),
      }}
    />
  );
}

/**
 * Il gradiente dell'onboarding (PromptIntro, CreationLoader) molto schiarito:
 * stesso fondo azzurro-verde-lilla mescolato col bianco, stesse tre macchie
 * sfocate che si muovono piano, ma piu' tenui.
 */
const ONBOARDING = `linear-gradient(rgba(255,255,255,0.55), rgba(255,255,255,0.55)), ${MESH_BG}`;

function Onboarding() {
  return (
    <>
      <div
        className="pu-blob-a absolute left-[8%] top-[40px] size-[380px] rounded-full opacity-[0.09] blur-[70px]"
        style={{ backgroundImage: TINTS.violet }}
      />
      <div
        className="pu-blob-b absolute right-[10%] top-[180px] size-[320px] rounded-full opacity-[0.08] blur-[70px]"
        style={{ backgroundImage: TINTS.green }}
      />
      <div
        className="pu-blob-c absolute left-[48%] -top-20 size-[360px] rounded-full opacity-[0.08] blur-[80px]"
        style={{ backgroundImage: "linear-gradient(135deg, #9C4EFF, #4285F4)" }}
      />
    </>
  );
}

const SFONDI = {
  onboarding: { fondo: ONBOARDING, Contenuto: Onboarding, chiaro: true },
  stelle: { fondo: "#0A0A0B", Contenuto: Stelle },
  antracite: { fondo: ANTRACITE, Contenuto: Antracite },
  perla: { fondo: PERLA, Contenuto: Perla, chiaro: true },
};

/** Lo sfondo e' chiaro? Allora sopra servono testi scuri. */
export function sfondoChiaro(variante) {
  return Boolean(SFONDI[variante]?.chiaro);
}

/**
 * Lo sfondo in cima alla pagina: `onboarding` (il gradiente dell'onboarding,
 * molto schiarito) e poi le varianti dei riferimenti: `stelle` (cielo quasi nero,
 * la prima immagine di riferimento), `antracite` (grigio scuro liscio, la
 * seconda) e `perla` (grigio chiaro, la terza: sopra vanno testi scuri, vedi
 * sfondoChiaro). Copre il contenitore in cui sta e continua per `coda` px sotto,
 * sfumando nel bianco (vedi sfumatura). `spazio` e' il padding in basso del
 * contenitore. Il contenitore va dentro uno `relative isolate`; lo sfondo
 * esce dai margini del layout (MainLayout + BotLayout: 2rem ai lati) e sale
 * dietro la barra del percorso (4rem + 0.5rem), cosi' non c'e' uno stacco tra la
 * barra bianca e lo sfondo.
 */
export function MeshBackdrop({ variante = "stelle", spazio = 112, coda = 900 }) {
  const maschera = sfumatura(spazio, coda);
  const { fondo, Contenuto } = SFONDI[variante] || SFONDI.stelle;
  return (
    <div
      aria-hidden="true"
      className="pointer-events-none absolute -inset-x-8 -top-[72px] -z-10 overflow-hidden"
      style={{ bottom: -coda, background: fondo, maskImage: maschera, WebkitMaskImage: maschera }}
    >
      <Contenuto />
      <div className="absolute inset-0 opacity-[0.05] mix-blend-screen" style={{ backgroundImage: GRANA }} />
    </div>
  );
}

/** Freccia disegnata a mano (la stessa della revisione dell'onboarding). */
export function HandArrow({ className = "" }) {
  return (
    <svg
      width="15"
      height="34"
      viewBox="0 0 14.6162 33.5002"
      fill="none"
      aria-hidden="true"
      className={`shrink-0 ${className}`}
    >
      <path
        d="M13.866 0.750204C5.19938 3.7502 -7.93395 14.3502 8.86604 32.7502M1.36575 30.7502L8.86604 32.7502V24.7502"
        stroke="#64748B"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** Nota scritta a mano con la freccia che punta in basso. */
export function HandNote({ children, className = "" }) {
  return (
    <div className={`flex items-start gap-2 pl-2 ${className}`}>
      <HandArrow className="mt-3" />
      <p
        className="text-sm leading-snug tracking-[0.7px] text-[#64748B]"
        style={{ fontFamily: "'Fuzzy Bubbles', cursive" }}
      >
        {children}
      </p>
    </div>
  );
}

const TESSERA = { xs: "h-7 w-7 rounded-lg", sm: "h-8 w-8 rounded-lg", md: "h-11 w-11 rounded-xl", lg: "h-14 w-14 rounded-2xl" };
const GLIFO = { xs: "h-3.5 w-3.5", sm: "h-4 w-4", md: "h-5 w-5", lg: "h-7 w-7" };
// Il logo del brand e' pieno: un filo piu' grande del glifo a tratto.
const LOGO = { xs: "h-4 w-4", sm: "h-[18px] w-[18px]", md: "h-6 w-6", lg: "h-8 w-8" };

// Su colori chiari (es. giallo) il logo va scuro, come nelle linee guida dei brand.
function chiaro(hex) {
  const n = parseInt(String(hex).replace("#", ""), 16);
  if (!Number.isFinite(n)) return false;
  return (0.299 * ((n >> 16) & 255) + 0.587 * ((n >> 8) & 255) + 0.114 * (n & 255)) / 255 > 0.7;
}

/**
 * L'icona di un'app su una tessera. I servizi con un marchio (app.brand)
 * hanno il loro logo e il loro colore ufficiali; le funzioni nostre hanno
 * l'icona a tratto sul gradiente dell'onboarding.
 */
export function AppTile({ app, size = "md", muted = false }) {
  const ombra = "shadow-[0_4px_10px_rgba(15,23,42,0.12)]";
  const Icon = app.icon;
  if (app.brand && !muted) {
    const { path, hex, sfondo, title } = app.brand;
    return (
      <div
        className={`flex shrink-0 items-center justify-center ${ombra} ${TESSERA[size]}`}
        style={{ background: sfondo || hex, color: chiaro(hex) ? "#0F172A" : "#FFFFFF" }}
      >
        {/* Senza logo nella libreria resta l'icona, ma sul colore del brand. */}
        {path ? (
          <svg viewBox="0 0 24 24" className={LOGO[size]} fill="currentColor" role="img" aria-label={title}>
            <path d={path} />
          </svg>
        ) : (
          <Icon className={GLIFO[size]} strokeWidth={2} aria-hidden="true" />
        )}
      </div>
    );
  }
  return (
    <div
      className={`flex shrink-0 items-center justify-center text-white ${ombra} ${TESSERA[size]}`}
      style={{ backgroundImage: muted ? TINTS.slate : TINTS[app.tint] || TINTS.violet }}
    >
      <Icon className={GLIFO[size]} strokeWidth={1.75} aria-hidden="true" />
    </div>
  );
}

const PILL = {
  attiva: { label: "Attiva", dot: "bg-[#19D163]", bg: "bg-[#19D163]/10", text: "text-[#0F7A3D]" },
  spenta: { label: "Spenta", dot: "bg-[#94A3B8]", bg: "bg-[#F1F5F9]", text: "text-[#475569]" },
  daConfigurare: { label: "Da configurare", dot: "bg-amber-500", bg: "bg-amber-50", text: "text-amber-800" },
  // Come «In arrivo» nella Panoramica.
  inArrivo: { label: "In arrivo", dot: "bg-amber-500", bg: "bg-amber-50", text: "text-[#92400E]" },
};

/**
 * Lo stato di un'app in una pillola: pallino colorato e parola.
 * `suColore`: sopra una superficie colorata la pillola e' bianca.
 */
export function StatusPill({ state, suColore = false }) {
  const pill = PILL[state] || PILL.spenta;
  const fondo = suColore ? "bg-white/90 shadow-sm" : pill.bg;
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium ${fondo} ${pill.text}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${pill.dot}`} aria-hidden="true" />
      {pill.label}
    </span>
  );
}

/** Lo stato di un'app a partire dalle sue impostazioni. */
export function statoApp(app, settings) {
  if (app.inArrivo) return "inArrivo";
  if (settings?.enabled) return "attiva";
  return app.ready(settings || {}) ? "spenta" : "daConfigurare";
}

/**
 * Intestazione di un passaggio, come la checklist del loader: cerchio con il
 * numero, nero con la spunta quando e' fatto.
 */
export function StepHeading({ number, done, title, subtitle, optional }) {
  return (
    <div className="flex items-center gap-3">
      <div
        className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-semibold transition-colors duration-300 ${
          done ? "bg-[#0F172A] text-[#F8FAFC]" : "bg-[#F3F4F6] text-[#020617]"
        }`}
      >
        {done ? <Check className="pu-pop-in h-4 w-4" aria-hidden="true" /> : number}
      </div>
      <div className="min-w-0">
        <p className="flex items-center gap-2 text-base font-semibold text-[#020617]">
          {title}
          {optional ? <span className="text-xs font-normal text-[#94A3B8]">facoltativo</span> : null}
        </p>
        {subtitle ? <p className="text-xs leading-4 tracking-[0.18px] text-[#64748B]">{subtitle}</p> : null}
      </div>
    </div>
  );
}

/** La card bianca dell'onboarding. */
export function BrandCard({ className = "", children, ...props }) {
  return (
    <div className={`rounded-[14px] border border-[#E2E8F0] bg-white ${className}`} {...props}>
      {children}
    </div>
  );
}
